import { useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import LoadingOverlay from "react-loading-overlay";
import {
  EverlakeLoadingOverlayStyle,
  EverlakeLoadingSpinner,
} from "components/General/EverlakeLoading";

import moment from "moment";
import config from "config/config";
import { useTranslation } from "react-i18next";

import { useDispatch, useSelector } from "react-redux";
import { getCommission } from "redux/slices/getCommissionSlice";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";

import { useRowPagination } from "hooks/useRowPagination";
import { useBreadcrumb } from "hooks/useBreadcrumb";

import { stringFormattingToCurrency } from "js/formatting";

import RowPagination from "components/General/RowPagination";
import SimpleDataRow from "components/General/SimpleDataRow";
import SortByDate from "components/General/SortByDate";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

const Commissions = () => {
  LoadingOverlay.propTypes = undefined;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  useBreadcrumb([{ name: "Commissions", url: "#" }]);

  const { user } = useSelector((state) => state.user);
  const { agentPolicies } = useContext(AgentData);
  const [orderBy, setOrderBy] = useState("");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [commissionInfo, setCommissionInfo] = useState({});
  const [isCommissionInfoLoading, setIsCommissionInfoLoading] = useState(false);

  const [commissionList, setCommissionList] = useState([]);
  const [isCommissionListLoading, setIsCommissionListLoading] = useState(false);

  useEffect(() => {
    let copyCommissions = [...commissionList];
    if (orderBy === "oldest") {
      copyCommissions?.sort(function (a, b) {
        if (
          new Date(a?.document?.dateOfDocument) <
          new Date(b?.document?.dateOfDocument)
        ) {
          return -1;
        }
        if (
          new Date(a?.document?.dateOfDocument) >
          new Date(b?.document?.dateOfDocument)
        ) {
          return 1;
        }
        return 0;
      });
    } else if (orderBy === "newest") {
      copyCommissions?.sort(function (a, b) {
        if (
          new Date(a?.document?.dateOfDocument) >
          new Date(b?.document?.dateOfDocument)
        ) {
          return -1;
        }
        if (
          new Date(a?.document?.dateOfDocument) <
          new Date(b?.document?.dateOfDocument)
        ) {
          return 1;
        }
        return 0;
      });
    }
    setCommissionList(copyCommissions);
  }, [orderBy]);

  useEffect(() => {
    setIsCommissionInfoLoading(true);
    setIsCommissionListLoading(true);

    dispatch(getCommission(user?.corpAgentId ?? user?.agentViewingId)).then(
      (response) => {
        setIsCommissionInfoLoading(false);
        setCommissionInfo(response?.payload);
      }
    );

    dispatch(
      getCorrespondenceList({
        policyId: "",
        agentId: user?.corpAgentId ?? user?.agentViewingId,
        beginDate: moment()
          .subtract(
            parseInt(config.COMMISSION_CORRESPODENCE_DURATION),
            "months"
          )
          .format("MM/DD/YYYY"),
        endDate: moment().format("MM/DD/YYYY"),
        docType: "COMMISSIONS",
        agentPolicies: agentPolicies,
      })
    )
      .then((response) => {
        setIsCommissionListLoading(false);
        let comStatements = [];
        if (response.meta.requestStatus === "fulfilled" && response.payload) {
          response.payload.allAlerts.forEach((a) => {
            if (a.document?.typeOfDocument?.toUpperCase() === "COMMISSIONS") {
              comStatements.push(a);
            }
          });
          if (comStatements) {
            setCommissionList(comStatements);
            setOrderBy("newest");
          }
        }
      })
      .catch((error) => {
        setIsCommissionListLoading(false);
        setOrderBy("newest");
        console.error("getCorrespondenceList error", error);
      });
  }, []);

  const { rowsForDisplay: agentCommisionRows, rowPaginationProps } =
    useRowPagination(commissionList);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("AgentCommisions-title")}</h1>
        <p>{t("AgentCommisions-subTitle")}</p>
        <hr />
      </div>

      <div className="container ui-component-spacing">
        <h2>{t("AgentCommisions-section-1-title")}</h2>
        <hr />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:gap-6">
          {/* year to date */}
          <LoadingOverlay
            active={isCommissionInfoLoading}
            spinner={<EverlakeLoadingSpinner />}
            styles={EverlakeLoadingOverlayStyle}
          >
            <div className="ui-card p-4 xl:p-6 grid grid-cols-1">
              <div>
                <div className="font-semibold">
                  {t("AgentCommisions-section-1-column-1-title")}
                </div>
                <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  <div className="w-full">
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {t("AgentCommisions-section-1-column-price-1", {
                        price: stringFormattingToCurrency(
                          commissionInfo?.commission?.yearToDateCommissionToDate
                        ),
                      })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-1")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {t("AgentCommisions-section-1-column-price-2", {
                        price: stringFormattingToCurrency(
                          commissionInfo?.commission?.yearToDateChargeBacks
                        ),
                      })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-2")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {isCommissionInfoLoading
                        ? "$0.00"
                        : t("AgentCommisions-section-1-column-price-3", {
                            price: stringFormattingToCurrency(
                              (parseFloat(
                                commissionInfo?.commission
                                  ?.yearToDateCommissionToDate
                              ) ?? 0) +
                                (parseFloat(
                                  commissionInfo?.commission
                                    ?.yearToDateChargeBacks
                                ) ?? 0)
                            ),
                          })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-3")}
                    </div>
                  </div>
                </div>
                <hr className="my-4" />
                <div className="mt-4 text-xxs">
                  {t("AgentCommisions-section-1-column-1-note-1")}
                </div>
              </div>
            </div>
          </LoadingOverlay>

          {/* Previous month earning */}
          <LoadingOverlay
            active={isCommissionInfoLoading}
            spinner={<EverlakeLoadingSpinner />}
            styles={EverlakeLoadingOverlayStyle}
          >
            <div className="ui-card p-4 xl:p-6 grid grid-cols-1">
              <div>
                <div className="font-semibold">
                  {t("AgentCommisions-section-1-column-2-title")}
                </div>
                <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  <div>
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {t("AgentCommisions-section-1-column-price-1", {
                        price: stringFormattingToCurrency(
                          commissionInfo?.commission?.cycleCommissionAmount ?? 0
                        ),
                      })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-1")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {t("AgentCommisions-section-1-column-price-2", {
                        price: stringFormattingToCurrency(
                          commissionInfo?.commission
                            ?.cycleCommissionChargedBack ?? 0
                        ),
                      })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-2")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-theme-text-secondary">
                      {isCommissionInfoLoading
                        ? "$0.00"
                        : t("AgentCommisions-section-1-column-price-3", {
                            price: stringFormattingToCurrency(
                              (parseFloat(
                                commissionInfo?.commission
                                  ?.cycleCommissionAmount
                              ) ?? 0) +
                                (parseFloat(
                                  commissionInfo?.commission
                                    ?.cycleCommissionChargedBack
                                ) ?? 0)
                            ),
                          })}
                    </div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentCommisions-section-1-column-text-3")}
                    </div>
                  </div>
                </div>
                <hr className="my-4" />
                <div className="mt-4 text-xxs">
                  {t("AgentCommisions-section-1-column-2-note-1")}
                </div>
              </div>
            </div>
          </LoadingOverlay>
        </div>
      </div>

      <div className="container ui-component-spacing">
        <h2>{t("AgentCommisions-section-2-title")}</h2>
        <hr />
        <Tab.Group selectedIndex={selectedTabIndex}>
          <div>
            <div className="ui-card sm:col-span-2 md:col-span-1 px-2 p-1 md:px-4">
              <Tab.List>
                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 0 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSelectedTabIndex(0);
                  }}
                >
                  {t("AgentCommisions-section-2-tab-1-title")}
                </Tab>

                <Tab
                  className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg ${
                    selectedTabIndex === 1 ? "" : "text-ui-blue"
                  }`}
                  onClick={() => {
                    setSelectedTabIndex(1);
                  }}
                >
                  {t("AgentCommisions-section-2-tab-2-title")}
                </Tab>
              </Tab.List>
            </div>

            <div className="mx-2 flex flex-col items-centers justify-end md:mx-4 md:flex-row md:space-x-4">
              <div className="flex flex-row items-center mt-4 xl:mt-6">
                <SortByDate orderBy={orderBy} setOrderBy={setOrderBy} />
              </div>
            </div>
          </div>
          <Tab.Panels className="mt-4 xl:mt-6">
            <Tab.Panel>
              <LoadingOverlay
                active={isCommissionListLoading}
                spinner={<EverlakeLoadingSpinner />}
                styles={EverlakeLoadingOverlayStyle}
              >
                {agentCommisionRows.map((item, idx) => (
                  <SimpleDataRow
                    key={`agent-commision-row-${idx}`}
                    type="commission"
                    data={item}
                  />
                ))}
                <RowPagination {...rowPaginationProps} />
              </LoadingOverlay>
            </Tab.Panel>

            <Tab.Panel>
              <div className="border border-dashed border-ui-gray p-6 my-6">
                <div className="text-center">
                  <div className="text-xl font-bold">
                    {t("AgentCommisions-section-2-tab-panel-text-1")}
                  </div>
                  <div className="mt-1 text-theme-text-quaternary text-sm">
                    {t("AgentCommisions-section-2-tab-panel-text-2")}
                  </div>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </>
  );
};

export default Commissions;
